import React, {FC, Suspense, useMemo, useState, lazy} from 'react'
import {Spinner} from 'react-bootstrap'
import {KTSVG} from '../../../../../_metronic/helpers'
import {MessageTrendsChartProps} from './types'
import LoadingFallback from './LoadingFallback'

const ReactApexChart = lazy(() => import('react-apexcharts'))

const MessageTrendsChart: FC<MessageTrendsChartProps> = React.memo(
  ({
    smsData,
    mmsData,
    cogData,
    smsTotalCount,
    mmsTotalCount,
    cogTotalCount,
    type,
    onTypeChange,
    isUserWhiteLabel,
    loading,
  }) => {
    const [showChart, setShowChart] = useState(false)

    const chartOptions: any = useMemo(
      () => ({
        chart: {
          type: 'area',
          stacked: false,
          height: 350,
          zoom: {type: 'x', enabled: false, autoScaleYaxis: true},
          toolbar: {show: false},
          animations: {enabled: true, speed: 400},
        },
        colors: ['#009ef7', '#50cd89', '#ffc700'],
        dataLabels: {enabled: false},
        markers: {size: 0},
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            inverseColors: false,
            opacityFrom: 0.5,
            opacityTo: 0,
            stops: [0, 90, 100],
          },
        },
        yaxis: {labels: {formatter: (val: number) => val?.toFixed(0)}},
        xaxis: {type: 'datetime'},
        tooltip: {shared: false, y: {formatter: (val: number) => val?.toFixed(0)}},
        legend: {position: 'top', horizontalAlign: 'left'},
      }),
      []
    )

    const chartSeries = useMemo(
      () => [
        {name: 'SMS', data: smsData},
        {name: 'MMS', data: mmsData},
        {name: 'COG', data: cogData},
      ],
      [smsData, mmsData, cogData]
    )

    const tabs = useMemo(
      () => [
        {id: 'qm', label: 'QM', icon: '/media/icons/duotune/navigationicon/messages.svg'},
        {id: 'bm', label: 'BM', icon: '/media/icons/duotune/navigationicon/quick-message.svg'},
        ...(!isUserWhiteLabel
          ? [{id: 'api', label: 'API', icon: '/media/icons/duotune/navigationicon/bulkmsg.svg'}]
          : []),
      ],
      [isUserWhiteLabel]
    )

    return (
      <div className='card'>
        <div className='card-header border-0 align-items-start p-6 pb-0'>
          <div className='card-title flex-column'>
            <h3 className='fw-bold fs-3 mb-1'>Messages Sent</h3>
            <span className='text-muted fs-7'>Overview of all sent messages</span>
          </div>

          <div className='card-toolbar'>
            <ul className='nav nav-tabs nav-pills border-0 gap-2' role='tablist'>
              {tabs.map((tab) => (
                <li key={tab.id} className='nav-item'>
                  <button
                    className={`nav-link btn btn-sm btn-flex btn-light-primary fw-semibold ${
                      type === tab.id ? 'active' : ''
                    }`}
                    onClick={() => onTypeChange(tab.id as 'qm' | 'bm' | 'api')}
                    type='button'
                  >
                    <KTSVG path={tab.icon} className='svg-icon-2 me-2' />
                    {tab.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className='card-body pt-5'>
          {/* Stats Summary */}
          <div className='d-flex flex-wrap gap-6 mb-6 p-4 bg-light rounded'>
            <div className='text-center' style={{minWidth: 80}}>
              <span className='fs-2x fw-bold text-primary d-block'>
                {loading ? (
                  <Spinner size='sm' animation='border' />
                ) : (
                  smsTotalCount?.toFixed(0) || 0
                )}
              </span>
              <span className='text-muted fs-7 fw-semibold text-uppercase'>SMS</span>
            </div>
            <div className='text-center' style={{minWidth: 80}}>
              <span className='fs-2x fw-bold text-success d-block'>
                {loading ? (
                  <Spinner size='sm' animation='border' />
                ) : (
                  mmsTotalCount?.toFixed(0) || 0
                )}
              </span>
              <span className='text-muted fs-7 fw-semibold text-uppercase'>MMS</span>
            </div>
            <div className='text-center' style={{minWidth: 80}}>
              <span className='fs-2x fw-bold text-warning d-block'>
                {loading ? (
                  <Spinner size='sm' animation='border' />
                ) : (
                  cogTotalCount?.toFixed(0) || 0
                )}
              </span>
              <span className='text-muted fs-7 fw-semibold text-uppercase'>COG</span>
            </div>
          </div>

          {/* Chart Area */}
          {!showChart ? (
            <div
              className='d-flex flex-column align-items-center justify-content-center py-12 cursor-pointer bg-light rounded border border-2 border-dashed border-gray-300'
              onClick={() => setShowChart(true)}
              role='button'
              tabIndex={0}
              style={{minHeight: 300}}
            >
              <KTSVG
                path='/media/icons/duotune/graphs/gra004.svg'
                className='svg-icon-5x svg-icon-primary mb-4'
              />
              <h5 className='fw-semibold text-gray-700 mb-2'>Click to view message trends</h5>
              <p className='text-muted fs-7'>Load detailed chart visualization</p>
            </div>
          ) : (
            <Suspense fallback={<LoadingFallback height={350} />}>
              <ReactApexChart
                options={chartOptions}
                series={chartSeries}
                type='area'
                height={350}
              />
            </Suspense>
          )}
        </div>
      </div>
    )
  }
)

MessageTrendsChart.displayName = 'MessageTrendsChart'

export default MessageTrendsChart
